import ReactGA from "react-ga4";
import { GA_TRACKING_ID } from "../ga";

/**
 * Google Analytics utility functions
 * Provides tracking functions for page views and custom events
 * Integrated with Cookiebot for GDPR compliance
 * Uses react-ga4 library for reliable GA4 integration
 */

// Check if Cookiebot consent is given
const hasConsent = () => {
  if (typeof window === "undefined" || !window.Cookiebot) {
    return false;
  }
  // Check if consent object exists and statistics cookies are consented
  if (!window.Cookiebot.consent) {
    return false;
  }
  return window.Cookiebot.consent.statistics === true;
};

// Extract campaign parameters from URL
const getCampaignParams = () => {
  if (typeof window === "undefined") {
    return {};
  }

  const params = new URLSearchParams(window.location.search);
  const campaignParams = {};

  // Standard UTM parameters (GA4 automatically captures these, but we'll include them explicitly)
  const utmParams = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
  ];

  utmParams.forEach((param) => {
    const value = params.get(param);
    if (value) {
      campaignParams[param] = value;
    }
  });

  // Custom campaign parameters (like 'tag', 'ref', 'source', etc.)
  const customParams = ["tag", "ref", "source", "campaign_id", "ad_id"];
  customParams.forEach((param) => {
    const value = params.get(param);
    if (value) {
      campaignParams[param] = value;
    }
  });

  return campaignParams;
};

// Initialize Google Analytics with Consent Mode
export const initGA = (trackingId) => {
  if (!trackingId || typeof window === "undefined") {
    console.warn("Google Analytics: Tracking ID not provided");
    return;
  }

  // Initialize dataLayer and gtag function first (before loading script)
  window.dataLayer = window.dataLayer || [];
  function gtag(...args) {
    window.dataLayer.push(args);
  }
  window.gtag = gtag;

  // Set default consent mode to 'denied' (GDPR compliant)
  // This means GA won't track until consent is given via Cookiebot
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "denied",
    personalization_storage: "denied",
    security_storage: "granted",
    wait_for_update: 500,
  });

  // Initialize react-ga4 with the tracking ID
  // This will handle script loading and initialization
  // Note: react-ga4 will use the gtag function we set up above
  try {
    ReactGA.initialize(trackingId, {
      // Don't use testMode in production - we want real tracking
      testMode: false,
      gtagOptions: {
        page_path: window.location.pathname,
      },
    });

    if (process.env.NODE_ENV === "development") {
      console.log("Google Analytics initialized with tracking ID:", trackingId);
      console.log("Waiting for Cookiebot consent...");
    }
  } catch (error) {
    console.error("Google Analytics: Failed to initialize", error);
  }
};

// Update consent mode based on Cookiebot consent
export const updateConsentMode = () => {
  if (typeof window === "undefined" || !window.gtag || !window.Cookiebot) {
    return;
  }

  // Check if consent object exists
  if (!window.Cookiebot.consent) {
    return;
  }

  const consent = window.Cookiebot.consent;

  // Update consent mode based on Cookiebot consent
  window.gtag("consent", "update", {
    ad_storage: consent.marketing === true ? "granted" : "denied",
    ad_user_data: consent.marketing === true ? "granted" : "denied",
    ad_personalization: consent.marketing === true ? "granted" : "denied",
    analytics_storage: consent.statistics === true ? "granted" : "denied",
    functionality_storage: consent.preferences === true ? "granted" : "denied",
    personalization_storage:
      consent.preferences === true ? "granted" : "denied",
    security_storage: "granted", // Always granted for security
  });

  // If analytics consent was just granted, send a pageview to start tracking
  if (consent.statistics === true && window.gtag) {
    // Small delay to ensure consent mode update is processed
    setTimeout(() => {
      trackPageView(window.location.pathname + window.location.search);

      if (process.env.NODE_ENV === "development") {
        console.log("Google Analytics: Consent granted, tracking started");
      }
    }, 100);
  }
};

// Track page views
export const trackPageView = (path) => {
  if (typeof window === "undefined") {
    return;
  }

  // Only track if consent is given (or if Cookiebot isn't loaded yet - will be blocked by consent mode)
  // Consent mode will handle blocking if consent isn't given
  // We still check hasConsent() to avoid unnecessary calls
  if (window.Cookiebot && !hasConsent()) {
    return;
  }

  try {
    // Extract campaign parameters from URL
    const campaignParams = getCampaignParams();

    // Build pageview parameters
    const pageviewParams = {
      page_path: path,
      // Include campaign parameters explicitly
      ...campaignParams,
    };

    // Use gtag directly for GA4 pageview tracking (more reliable)
    if (window.gtag) {
      window.gtag("config", GA_TRACKING_ID, pageviewParams);
    }
    // Also use react-ga4 for compatibility
    ReactGA.send({ hitType: "pageview", page: path });

    // Log campaign parameters in development
    if (process.env.NODE_ENV === "development" && Object.keys(campaignParams).length > 0) {
      console.log("Google Analytics: Campaign parameters detected:", campaignParams);
    }
  } catch (error) {
    console.error("Google Analytics: Failed to track page view", error);
  }
};

// Track custom events
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window === "undefined") {
    return;
  }

  // Only track if consent is given
  if (!hasConsent()) {
    return;
  }

  try {
    ReactGA.event(eventName, eventParams);
  } catch (error) {
    console.error("Google Analytics: Failed to track event", error);
  }
};

// Track button clicks
export const trackButtonClick = (buttonName, location = "") => {
  trackEvent("button_click", {
    button_name: buttonName,
    location: location || window.location.pathname,
  });
};

// Track article views
export const trackArticleView = (articleId, articleTitle) => {
  trackEvent("article_view", {
    article_id: articleId,
    article_title: articleTitle,
  });
};

// Track search queries
export const trackSearch = (searchQuery, resultsCount = 0) => {
  trackEvent("search", {
    search_term: searchQuery,
    results_count: resultsCount,
  });
};

// Track language changes
export const trackLanguageChange = (newLanguage) => {
  trackEvent("language_change", {
    language: newLanguage,
  });
};

// Track external link clicks
export const trackExternalLink = (url) => {
  trackEvent("external_link_click", {
    link_url: url,
  });
};

// Track campaign-specific events (includes campaign parameters if present)
export const trackCampaignEvent = (eventName, eventParams = {}) => {
  const campaignParams = getCampaignParams();
  trackEvent(eventName, {
    ...eventParams,
    ...campaignParams,
  });
};
